import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaArrowRightLong } from "react-icons/fa6";
import { useTranslation } from "react-i18next";


export default function QualitySection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("objective");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const objectiveFeatures = t("quality_objective_features").split(",");
  const goalsFeatures = t("quality_goals_features").split(",");

  const objectiveImage = "/1.webp";
  const goalsImage = "/polycarbonate9.webp";

  return (
    <section
      ref={sectionRef}
      className={` py-14 md:py-16 transition-all duration-1000 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-8 items-start">

          {/* LEFT IMAGE */}
          <div className="flex-1 w-full">
            <div className="relative overflow-hidden rounded-3xl aspect-[4/3] sm:aspect-[5/3] lg:aspect-auto lg:h-[560px]">
              <img
                src={objectiveImage}
                alt="Smart Automated Greenhouse"
                loading="eager"
                decoding="async"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${activeTab === "objective" ? "opacity-100" : "opacity-0"
                  }`}
              />
              <img
                src={goalsImage}
                alt="R&D Greenhouse"
                loading="lazy"
                decoding="async"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${activeTab === "goals" ? "opacity-100" : "opacity-0"
                  }`}
              />
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="flex-1 w-full flex flex-col">
            <h2 className="text-[32px] sm:text-[38px] lg:text-[45px] font-semibold text-[#002F3F] mb-4 leading-tight">
              {t("quality_heading")}
            </h2>

            {/* TABS */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <button
                onClick={() => setActiveTab("objective")}
                className={`px-5 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer w-full sm:w-auto ${activeTab === "objective"
                  ? "bg-[#7EDE56] text-[#002F3F]"
                  : "border border-black text-black hover:bg-gray-100"
                  }`}
              >
                {t("quality_tab_objective")}
              </button>

              <button
                onClick={() => setActiveTab("goals")}
                className={`px-5 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer w-full sm:w-auto ${activeTab === "goals"
                  ? "bg-[#002F3F] text-white"
                  : "border border-black text-black hover:bg-gray-100"
                  }`}
              >
                {t("quality_tab_goals")}
              </button>
            </div>

            <div className="h-px bg-gray-300 mb-4" />

            {/* CONTENT */}
            <div className="flex flex-col justify-between flex-1">
              <div>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-xl mb-4">
                  {activeTab === "objective"
                    ? t("quality_objective_text")
                    : t("quality_goals_text")}
                </p>

                <ul className="space-y-3">
                  {(activeTab === "objective" ? objectiveFeatures : goalsFeatures).map(
                    (item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-xl text-[#002F3F]">●</span>
                        <span className="text-[#002F3F] font-medium">{item}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>

              <div className="pt-4">
                <Link
                  to={activeTab === "objective" ? "/automation-detail" : "/research-detail"}
                  className="inline-block mt-2"
                >
                  <button className="group inline-flex items-center gap-2.5 bg-[#7EDE56] text-[#002F3F] hover:text-white cursor-pointer px-7 py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 hover:bg-[#002F3F] hover:gap-4">
                    {t("quality_cta")}
                    <FaArrowRightLong className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
